import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from '@mui/material';
import { connect } from 'react-redux';
import { getCodebasesByDaoId } from 'redux/actions/dao.action';
import { withRouter } from 'react-router-dom';
import Web3 from 'web3';
import CodebaseLicensingProcessorV2ABI from 'abis/NFTLicenseMarketplaceMultipleCoin';

// MinimalERC20ABI để lấy symbol và decimals của token
const MinimalERC20ABI = [
  {
    inputs: [],
    name: 'symbol',
    outputs: [{ internalType: 'string', name: '', type: 'string' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'decimals',
    outputs: [{ internalType: 'uint8', name: '', type: 'uint8' }],
    stateMutability: 'view',
    type: 'function',
  },
];

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

const SolutionsViewClaimTab = ({ currentDao, chainId, getCodebasesByDaoId, history }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [solutionsWithAmounts, setSolutionsWithAmounts] = useState([]);
  const [hasMetaMask, setHasMetaMask] = useState(false);
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [isCorrectChain, setIsCorrectChain] = useState(false);
  const [tokenSymbols, setTokenSymbols] = useState({});

  // Helper functions để lấy token metadata
  const normalize = (a) => ((a || '').toLowerCase() === '0x0' ? ZERO_ADDRESS : (a || '').toLowerCase());

  const getNativeSymbol = (chainId) => {
    const id = Number(chainId);
    if (id === 56) return 'BNB';
    if (id === 137 || id === 80001) return 'MATIC';
    if (id === 7001) return 'ZETA';
    return 'ETH';
  };

  const fetchTokenMeta = async (addr, chainId) => {
    const key = normalize(addr);
    if (key === ZERO_ADDRESS) {
      return { symbol: getNativeSymbol(chainId), decimals: 18 };
    }
    const web3 = new Web3(window.ethereum);
    const token = new web3.eth.Contract(MinimalERC20ABI, key);
    let symbol = '';
    let decimals = 18;
    try {
      symbol = await token.methods.symbol().call();
    } catch (_) {}
    try {
      decimals = await token.methods.decimals().call();
    } catch (_) {}
    return { symbol, decimals: Number(decimals || 18) };
  };
  useEffect(() => {
    setHasMetaMask(typeof window.ethereum !== 'undefined');
    if (window.ethereum) {
      const handleAccountsChanged = async (accounts) => {
        console.log('Account changed:', accounts);
        await checkWalletConnection();
        // Reload solutions data when account changes
        if (currentDao._id) {
          await reloadSolutionsData();
        }
      };

      const handleChainChanged = async (chainId) => {
        await checkWalletConnection();
        // Reload solutions data when chain changes
        if (currentDao._id) {
          await reloadSolutionsData();
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, [currentDao._id]);

  useEffect(() => {
    checkWalletConnection();
  }, []);

  const reloadSolutionsData = async () => {
    if (!currentDao._id) return;
    setLoading(true);
    setError('');
    try {
      const data = await getCodebasesByDaoId(currentDao._id);
      const solutions = data?.data || [];

      // Check current wallet connection state
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      const isConnected = accounts.length > 0;

      if (isConnected) {
        const web3 = new Web3(window.ethereum);
        const networkId = await web3.eth.net.getId();
        const expectedChainId = parseInt(chainId || '0', 10);
        const isCorrectChain = networkId === expectedChainId;

        if (isCorrectChain) {
          await fetchWithdrawableAmounts(solutions);
        } else {
        }
      } else {
        console.log('Wallet not connected, skipping withdrawable amounts');
      }
    } catch (err) {
      console.error('Error reloading solutions:', err);
      setError(err.message || 'Error fetching solutions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadSolutionsData();
  }, [getCodebasesByDaoId, currentDao, isWalletConnected, isCorrectChain]);

  const checkWalletConnection = async () => {
    if (!window.ethereum) {
      setHasMetaMask(false);
      setIsWalletConnected(false);
      return;
    }

    try {
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      const isConnected = accounts.length > 0;
      setIsWalletConnected(isConnected);

      if (isConnected) {

        try {
          const web3 = new Web3(window.ethereum);
          const networkId = await web3.eth.net.getId();
          const expectedChainId = parseInt(chainId || '0', 10);
          const isCorrectChain = networkId === expectedChainId;
          setIsCorrectChain(isCorrectChain);
        } catch (error) {
          console.error('Error checking chain:', error);
          setIsCorrectChain(false);
        }
      } else {
        setIsCorrectChain(false);
      }
    } catch (error) {
      console.error('Error checking wallet connection:', error);
      setIsWalletConnected(false);
      setIsCorrectChain(false);
    }
  };

  const connectWallet = async () => {
    try {
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      await checkWalletConnection();
      // Reload solutions data after connecting wallet
      if (currentDao._id) {
        await reloadSolutionsData();
      }
    } catch (err) {
      console.error('Failed to connect wallet:', err);
    }
  };

  const switchChain = async () => {
    try {
      const expectedChainId = parseInt(chainId || '0', 10);

      if (isNaN(expectedChainId) || expectedChainId === 0) {
        throw new Error('Invalid chain ID');
      }

      const chainIdHex = '0x' + expectedChainId.toString(16);

      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: chainIdHex }],
      });

      // Reload solutions data after switching chain
      if (currentDao._id) {
        await reloadSolutionsData();
      }
    } catch (error) {
      if (error.code === 4902) {
        try {
          const expectedChainId = parseInt(chainId || '0', 10);
          const chainIdHex = '0x' + expectedChainId.toString(16);

          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: chainIdHex,
                chainName: expectedChainId === 56 ? 'BSC' : 'Polygon',
                nativeCurrency:
                  expectedChainId === 56
                    ? {
                        name: 'BNB',
                        symbol: 'BNB',
                        decimals: 18,
                      }
                    : {
                        name: 'MATIC',
                        symbol: 'MATIC',
                        decimals: 18,
                      },
                rpcUrls: expectedChainId === 56 ? ['https://bsc-dataseed.binance.org/'] : ['https://polygon-rpc.com/'],
                blockExplorerUrls: expectedChainId === 56 ? ['https://bscscan.com/'] : ['https://polygonscan.com/'],
              },
            ],
          });

          // Reload solutions data after adding chain
          if (currentDao._id) {
            await reloadSolutionsData();
          }
        } catch (addError) {
          console.error('Error adding chain:', addError);
        }
      } else {
        console.error('Error switching chain:', error);
      }
    }
  };

  const fetchWithdrawableAmounts = async (solutions) => {
    try {
      if (!window.ethereum) {
        setError('MetaMask is not installed');
        return;
      }

      const web3 = new Web3(window.ethereum);
      const accounts = await web3.eth.getAccounts();
      if (!accounts || accounts.length === 0) {
        setError('No wallet connected');
        return;
      }

      const userAddress = accounts[0];

      // Flatten all claimable items from all solutions
      const allClaimableItems = [];

      await Promise.all(
        solutions.map(async (solution) => {
          const contractAddress = solution.nftMarketPlaceAddress;
          const contract = new web3.eth.Contract(CodebaseLicensingProcessorV2ABI, contractAddress);

          try {
            // Lấy danh sách supported currencies
            const supportedCurrencies = await contract.methods.getSupportedCurrencies().call();

            // Fetch token symbols cho tất cả currencies
            const symbols = {};
            for (const currencyAddr of supportedCurrencies) {
              try {
                const meta = await fetchTokenMeta(currencyAddr, chainId);
                symbols[normalize(currencyAddr)] = meta.symbol;
              } catch (error) {
                console.error('Error fetching token symbol for', currencyAddr, error);
                symbols[normalize(currencyAddr)] = '';
              }
            }
            setTokenSymbols((prev) => ({ ...prev, ...symbols }));

            // Lấy withdrawable amounts cho từng currency
            for (const currencyAddr of supportedCurrencies) {
              try {
                const { withdrawableDevPartner, withdrawableLPHolder, withdrawableDAOMember } = await contract.methods
                  .getClaimerWithdrawable(userAddress, currencyAddr)
                  .call();

                if (withdrawableDevPartner > 0 || withdrawableLPHolder > 0 || withdrawableDAOMember > 0) {
                  allClaimableItems.push({
                    currencyAddress: currencyAddr,
                    currencySymbol:
                      symbols[normalize(currencyAddr)] ||
                      (currencyAddr === '0x0000000000000000000000000000000000000000'
                        ? getNativeSymbol(chainId)
                        : Web3.utils.toChecksumAddress(currencyAddr)),
                    withdrawableDevPartner,
                    withdrawableLPHolder,
                    withdrawableDAOMember,
                    solutionName: solution.name || solution.title || 'No name',
                    solutionSlug: solution.slug || solution.code || solution._id || solution.id,
                  });
                }
              } catch (err) {
                console.error(`Error fetching withdrawable amounts for ${currencyAddr}:`, err);
              }
            }
          } catch (err) {
            console.error(`Error fetching withdrawable amounts for ${contractAddress}:`, err);
          }
        }),
      );

      setSolutionsWithAmounts(allClaimableItems);
    } catch (err) {
      console.error('Error in fetchWithdrawableAmounts:', err);
      setError(err.message || 'Error fetching withdrawable amounts');
    }
  };

  const handleViewClaim = (slug) => {
    history.push(`/code-base/${slug}`);
  };

  if (!hasMetaMask) {
    return (
      <Box textAlign="center" p={3}>
        <Typography variant="body1" gutterBottom sx={{ color: '#fcfcfc' }}>
          Please install MetaMask to access this content
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#05e4bc',
            '&:hover': { backgroundColor: '#04c9a5' },
            borderRadius: '12px',
            textTransform: 'none',
            py: 1.5,
          }}
          href="https://metamask.io"
          target="_blank"
          rel="noopener noreferrer"
        >
          Install MetaMask
        </Button>
      </Box>
    );
  }

  if (!isWalletConnected) {
    return (
      <Box textAlign="center" p={3}>
        <Typography variant="body1" gutterBottom sx={{ color: '#fcfcfc' }}>
          Please connect your wallet to access this content
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#05e4bc',
            '&:hover': { backgroundColor: '#04c9a5' },
            borderRadius: '12px',
            textTransform: 'none',
            py: 1.5,
          }}
          onClick={connectWallet}
        >
          Connect Wallet
        </Button>
      </Box>
    );
  }

  if (!isCorrectChain) {
    return (
      <Box textAlign="center" p={3}>
        <Typography variant="body1" gutterBottom sx={{ color: '#fcfcfc' }}>
          Please switch to the correct network to access this content
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#05e4bc',
            '&:hover': { backgroundColor: '#04c9a5' },
            borderRadius: '12px',
            textTransform: 'none',
            py: 1.5,
            mt: 2,
          }}
          onClick={switchChain}
        >
          Switch Network
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h6" sx={{ mb: 3, pb: 2, color: '#fff', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        Solutions - View & Claim
      </Typography>

      {loading && <CircularProgress />}
      {error && <Typography color="error">{error}</Typography>}
      {!loading && !error && (
        <>
          <TableContainer component={Paper} sx={{ background: '#282f38' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#00EDC3' }}>#</TableCell>
                  <TableCell sx={{ color: '#00EDC3' }}>Solution Name</TableCell>
                  <TableCell sx={{ color: '#00EDC3' }}>Token</TableCell>
                  <TableCell sx={{ color: '#00EDC3' }}>Withdrawable Dev Partner</TableCell>
                  <TableCell sx={{ color: '#00EDC3' }}>Withdrawable LP Holder</TableCell>
                  <TableCell sx={{ color: '#00EDC3' }}>Withdrawable DAO Member</TableCell>
                  <TableCell sx={{ color: '#00EDC3' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {solutionsWithAmounts.length > 0 ? (
                  solutionsWithAmounts.map((item, idx) => (
                    <TableRow key={`${item.solutionSlug}-${item.currencyAddress}-${idx}`}>
                      <TableCell sx={{ color: '#e7e3e3' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ color: '#e7e3e3' }}>{item.solutionName}</TableCell>
                      <TableCell sx={{ color: '#e7e3e3' }}>{item.currencySymbol}</TableCell>
                      <TableCell sx={{ color: '#e7e3e3' }}>
                        {item.withdrawableDevPartner
                          ? Web3.utils.fromWei(item.withdrawableDevPartner.toString(), 'ether')
                          : '0'}
                      </TableCell>
                      <TableCell sx={{ color: '#e7e3e3' }}>
                        {item.withdrawableLPHolder
                          ? Web3.utils.fromWei(item.withdrawableLPHolder.toString(), 'ether')
                          : '0'}
                      </TableCell>
                      <TableCell sx={{ color: '#e7e3e3' }}>
                        {item.withdrawableDAOMember
                          ? Web3.utils.fromWei(item.withdrawableDAOMember.toString(), 'ether')
                          : '0'}
                      </TableCell>
                      <TableCell sx={{ color: '#e7e3e3' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleViewClaim(item.solutionSlug)}
                          sx={{ background: '#00EDC3', color: '#282f38', fontWeight: 500 }}
                        >
                          View & Claim
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                      No claimable amounts found. Please ensure you are connected to the correct wallet and network.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default connect(null, { getCodebasesByDaoId })(withRouter(SolutionsViewClaimTab));
